import { Box, Button, Flex, Text } from '@chakra-ui/react'
import { motion } from 'framer-motion'

import { H2 } from '@/components/ui/Headings'
import { useBrandContext } from '@/context/brand-context'
import '@/components/ambient/animations.css'

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } }
}

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: 'easeOut' as const }
  }
}

const glassCard = {
  bg: 'rgba(18, 18, 26, 0.6)',
  backdropFilter: 'blur(12px)',
  borderRadius: '14px',
  border: '1px solid',
  borderColor: 'rgba(255, 255, 255, 0.08)',
  transition: 'all 0.3s ease',
  _hover: {
    borderColor: 'rgba(0, 112, 243, 0.25)',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.3)'
  }
}

const CHART_LABEL_WIDTH = '112px'
const CHART_VALUE_WIDTH = '38px'

interface SentimentStepProps {
  onBack: () => void
  onContinue: () => void
}

export default function SentimentStep({
  onBack,
  onContinue
}: SentimentStepProps) {
  const { brandAnalysis } = useBrandContext()
  const sentiment = brandAnalysis?.sentiment
  const brandName = brandAnalysis?.brand.name ?? 'Brand'

  if (!sentiment) return null

  const regions = sentiment.audienceDemographics.topRegions.map((r) =>
    typeof r === 'string' ? { name: r, percentage: 0 } : r
  )
  const ageGroups = sentiment.audienceDemographics.ageGroups.slice(0, 3)
  const topRegions = regions.slice(0, 3)
  const hasRegionPercentages = regions.some((r) => r.percentage > 0)
  const genderSplit = sentiment.audienceDemographics.genderSplit

  return (
    <motion.div
      variants={staggerContainer}
      initial="initial"
      animate="animate"
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '980px',
        margin: '0 auto',
        padding: '0 24px'
      }}
    >
      {/* Header */}
      <motion.div variants={fadeInUp}>
        <Box mb="10px">
          <H2 color="text.primary" fontWeight={300}>
            Brand Intelligence
          </H2>
          <Text color="white" textStyle="bodySuperSmall" mt="2px">
            How {brandName} resonates online
          </Text>
        </Box>
      </motion.div>

      <Box
        display="grid"
        gridTemplateColumns={{ base: '1fr', md: 'repeat(2, minmax(0, 1fr))' }}
        gap="12px"
        w="100%"
        mb="12px"
        alignItems="start"
      >
        <motion.div variants={fadeInUp}>
          <Flex direction="column" gap="12px" h="100%">
            <Box {...glassCard} px="14px" py="11px">
              <Text color="white" textStyle="label" mb="8px">
                Age Split
              </Text>
              {ageGroups.length > 0 ? (
                <Flex gap="6px" flexDirection="column">
                  {ageGroups.map((group, i) => (
                    <Flex key={i} alignItems="center" gap="8px">
                      <Text
                        color="text.secondary"
                        textStyle="caption"
                        w={CHART_LABEL_WIDTH}
                        pr="6px"
                        flexShrink={0}
                        whiteSpace="nowrap"
                      >
                        {group.range}
                      </Text>
                      <Box
                        flex="1"
                        h="3px"
                        bg="whiteAlpha.100"
                        borderRadius="2px"
                        overflow="hidden"
                      >
                        <Box
                          h="100%"
                          w={`${group.percentage}%`}
                          bg="accent.400"
                          borderRadius="2px"
                          transition="width 1s ease-out"
                        />
                      </Box>
                      <Text
                        color="text.tertiary"
                        textStyle="caption"
                        w={CHART_VALUE_WIDTH}
                        textAlign="right"
                        whiteSpace="nowrap"
                      >
                        {group.percentage}%
                      </Text>
                    </Flex>
                  ))}
                </Flex>
              ) : (
                <Text color="text.tertiary" textStyle="caption">
                  No age data
                </Text>
              )}
            </Box>
            <Box {...glassCard} px="14px" py="11px">
              <Text color="white" textStyle="label" mb="8px">
                Brand Buzz
              </Text>
              <Flex
                flexWrap="wrap"
                gap="5px"
                justifyContent="center"
                alignItems="center"
                minH="60px"
              >
                {sentiment.wordCloud.slice(0, 8).map((item, i) => {
                  const scale = 0.75 + (item.weight / 100) * 0.5
                  const opacity = 0.5 + (item.weight / 100) * 0.5
                  return (
                    <Text
                      key={i}
                      color="text.primary"
                      style={{
                        fontSize: `${10 * scale}px`,
                        opacity,
                        lineHeight: '1.4'
                      }}
                      px="3px"
                      transition="all 0.2s ease"
                      cursor="default"
                      _hover={{
                        color: 'accent.400',
                        textShadow: '0 0 8px rgba(0, 112, 243, 0.4)'
                      }}
                    >
                      {item.word}
                    </Text>
                  )
                })}
              </Flex>
            </Box>
            <Box {...glassCard} px="14px" py="11px">
              <Text color="white" textStyle="label" mb="8px">
                Main Competitors
              </Text>
              {sentiment.competitors.length > 0 ? (
                <Flex flexWrap="wrap" gap="6px">
                  {sentiment.competitors.slice(0, 4).map((comp, i) => (
                    <Box
                      key={i}
                      px="10px"
                      py="4px"
                      bg="bg.surface"
                      borderRadius="6px"
                      border="1px solid"
                      borderColor="border.subtle"
                      transition="all 0.2s ease"
                      _hover={{
                        borderColor: 'rgba(0, 112, 243, 0.3)',
                        bg: 'rgba(0, 112, 243, 0.05)'
                      }}
                    >
                      <Text color="white" textStyle="caption">
                        {comp.name}
                      </Text>
                    </Box>
                  ))}
                </Flex>
              ) : (
                <Text color="text.tertiary" textStyle="caption">
                  No competitor data
                </Text>
              )}
            </Box>
          </Flex>
        </motion.div>

        <motion.div variants={fadeInUp}>
          <Flex direction="column" gap="12px" h="100%">
            <Box {...glassCard} px="14px" py="11px">
              <Text color="white" textStyle="label" mb="8px">
                Top Regions
              </Text>
              {topRegions.length > 0 ? (
                hasRegionPercentages ? (
                  <Flex gap="6px" flexDirection="column">
                    {topRegions.map((region, i) => (
                      <Flex key={i} alignItems="center" gap="8px">
                        <Text
                          color="text.secondary"
                          textStyle="caption"
                          w={CHART_LABEL_WIDTH}
                          pr="6px"
                          flexShrink={0}
                          whiteSpace="nowrap"
                        >
                          {region.name}
                        </Text>
                        <Box
                          flex="1"
                          h="3px"
                          bg="whiteAlpha.100"
                          borderRadius="2px"
                          overflow="hidden"
                        >
                          <Box
                            h="100%"
                            w={`${region.percentage}%`}
                            bg="rgba(59, 158, 255, 0.75)"
                            borderRadius="2px"
                            transition="width 1s ease-out"
                          />
                        </Box>
                        <Text
                          color="text.tertiary"
                          textStyle="caption"
                          w={CHART_VALUE_WIDTH}
                          textAlign="right"
                          whiteSpace="nowrap"
                        >
                          {region.percentage}%
                        </Text>
                      </Flex>
                    ))}
                  </Flex>
                ) : (
                  <Flex gap="5px" flexWrap="wrap">
                    {topRegions.map((region, i) => (
                      <Text
                        key={i}
                        color="white"
                        textStyle="caption"
                        bg="bg.surface"
                        px="8px"
                        py="3px"
                        borderRadius="5px"
                      >
                        {region.name}
                      </Text>
                    ))}
                  </Flex>
                )
              ) : (
                <Text color="text.tertiary" textStyle="caption">
                  No region data
                </Text>
              )}
            </Box>
            <Box {...glassCard} px="14px" py="11px">
              <Text color="white" textStyle="label" mb="8px">
                Gender Split
              </Text>
              {genderSplit ? (
                <Box>
                  <Flex h="5px" borderRadius="3px" overflow="hidden">
                    <Box
                      bg="accent.400"
                      w={`${genderSplit.male}%`}
                      transition="width 1s ease-out"
                    />
                    <Box
                      bg="rgba(168, 85, 247, 0.7)"
                      w={`${genderSplit.female}%`}
                      transition="width 1s ease-out"
                    />
                    <Box
                      bg="whiteAlpha.300"
                      w={`${genderSplit.other}%`}
                      transition="width 1s ease-out"
                    />
                  </Flex>
                  <Flex gap="8px" mt="4px" flexWrap="wrap">
                    <Flex alignItems="center" gap="4px">
                      <Box w="6px" h="6px" borderRadius="1px" bg="accent.400" />
                      <Text color="text.tertiary" textStyle="caption">
                        {genderSplit.male}% M
                      </Text>
                    </Flex>
                    <Flex alignItems="center" gap="4px">
                      <Box
                        w="6px"
                        h="6px"
                        borderRadius="1px"
                        bg="rgba(168, 85, 247, 0.7)"
                      />
                      <Text color="text.tertiary" textStyle="caption">
                        {genderSplit.female}% F
                      </Text>
                    </Flex>
                    <Flex alignItems="center" gap="4px">
                      <Box
                        w="6px"
                        h="6px"
                        borderRadius="1px"
                        bg="whiteAlpha.300"
                      />
                      <Text color="text.tertiary" textStyle="caption">
                        {genderSplit.other}% Other
                      </Text>
                    </Flex>
                  </Flex>
                </Box>
              ) : (
                <Text color="text.tertiary" textStyle="caption">
                  No gender data
                </Text>
              )}
            </Box>
            <Box {...glassCard} px="14px" py="11px">
              <Text color="white" textStyle="label" mb="8px">
                Trending Topics
              </Text>
              <Flex flexWrap="wrap" gap="6px">
                {sentiment.trendingTopics.slice(0, 6).map((topic, i) => (
                  <Box
                    key={i}
                    px="10px"
                    py="4px"
                    bg="bg.surface"
                    borderRadius="6px"
                    border="1px solid"
                    borderColor="border.subtle"
                    transition="all 0.2s ease"
                    _hover={{
                      borderColor: 'rgba(0, 112, 243, 0.3)',
                      bg: 'rgba(0, 112, 243, 0.05)'
                    }}
                  >
                    <Text color="white" textStyle="caption">
                      {topic}
                    </Text>
                  </Box>
                ))}
              </Flex>
            </Box>
          </Flex>
        </motion.div>
      </Box>

      {/* Spacer for fixed button */}
      <Box h="88px" />

      <Box
        position="fixed"
        bottom="0"
        left="0"
        right="0"
        zIndex={10}
        px="24px"
        pb={{ base: '16px', md: '20px' }}
        pt="12px"
        bg="linear-gradient(to top, rgba(10, 10, 15, 1) 60%, rgba(10, 10, 15, 0))"
      >
        <Flex
          gap="12px"
          justifyContent="center"
          w="100%"
          maxW="420px"
          mx="auto"
        >
          <Button
            h="48px"
            minW="132px"
            textStyle="body"
            fontWeight={500}
            borderRadius="10px"
            bg="bg.surface"
            color="text.primary"
            border="1px solid"
            borderColor="border.subtle"
            _hover={{
              bg: 'rgba(255, 255, 255, 0.06)',
              borderColor: 'border.strong'
            }}
            transition="all 0.3s ease"
            onClick={onBack}
          >
            Go Back
          </Button>
          <Button
            h="48px"
            flex="1"
            textStyle="body"
            fontWeight={500}
            borderRadius="10px"
            bg="accent.500"
            color="white"
            border="1px solid"
            borderColor="accent.500"
            className="animate-glow-pulse"
            _hover={{
              bg: 'accent.400',
              transform: 'translateY(-1px)',
              boxShadow: '0 0 30px rgba(0, 112, 243, 0.3)'
            }}
            transition="all 0.3s ease"
            onClick={onContinue}
          >
            Continue
          </Button>
        </Flex>
      </Box>
    </motion.div>
  )
}
